// src/services/profileService.ts
/*
// プロファイル情報を扱うサービス
// 取得した情報はキャッシュします。
*/
// doc, getDoc, setDoc, serverTimestamp に加えて、updateDoc と arrayUnion をインポート
import { doc, getDoc, setDoc, serverTimestamp, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, appId } from '../firebase';
import useSWR, { mutate } from 'swr';
import { Profile } from '../types'; // Profile型をインポート
import { logger } from '../utils/logger';

/** キャッシュの名前と期間*/
const CACHE_KEY = 'user-profile';
const CACHE_EXPIRE_MS = 1000 * 60 * 60 * 24; // 1日


/**
 * Firebaseにプロフィールを保存・更新する関数
 * - nickname, birthday を上書き
 * - 更新時刻も serverTimestamp で追記
 * @param nickname - ユーザーのニックネーム
 * @param birthday - ユーザーの誕生日
 * @param userId - ユーザーID
 * @param email - email
 */
export const updateProfile = async (nickname: string, birthday: string, userId: string, email:string) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/userData/profile`);

    await setDoc(
      userRef,
      {
        nickname,
        lastNicknameChange: serverTimestamp(),
        birthday,
        lastBirthdayChange: serverTimestamp(),
        email,
      },
      { merge: true }
    );
    // SWRキャッシュも即時更新（次回取得不要）
    mutate(['user-profile', userId]);
  } catch (error: any) { // エラーの型も指定
    logger.error('プロフィール更新に失敗しました:', error);
    throw error;
  }
};


/**
 * Firebaseにプロフィールを保存・更新する関数
 * - nickname, birthday を上書き
 * - 更新時刻も serverTimestamp で追記
 * @param nickname - ユーザーのニックネーム
 * @param birthday - ユーザーの誕生日
 * @param uid - ユーザーID
 * @param quizzes - クイズデータ (より具体的な型に置き換えることを推奨)
 */
export const updateProfile2 = async (nickname: string, birthday: string, uid: string, quizzes: any) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${uid}/userData/profile`);

    await setDoc(
      userRef,
      {
        nickname,
        lastNicknameChange: serverTimestamp(),
        birthday,
        lastBirthdayChange: serverTimestamp(),
        quizzes,
      },
      { merge: true }
    );

    // SWRキャッシュも即時更新（次回取得不要）
    mutate(['user-profile', uid]);
  } catch (error: any) { // エラーの型も指定
    logger.error('プロフィール更新に失敗しました:', error);
    throw error;
  }
};

/**
 * ユーザープロフィールに作成したクイズのIDを追加する関数
  * @param userId - ユーザーID
    * @param quizId - 追加するクイズのID
      */
export const addQuizIdToProfile = async (userId: string, quizId: string): Promise<void> => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/userData/profile`);
    // arrayUnion を使用して、既存の配列に要素を重複なく追加
    await updateDoc(userRef, {
      addedQuizzes: arrayUnion(quizId)
    });
    mutate(['user-profile', userId]); // プロフィールキャッシュを更新
    logger.log(`Quiz ID ${quizId} added to user ${userId}'s profile.`);
  } catch (error) {
    logger.error(`Failed to add quiz ID ${quizId} to user ${userId}'s profile:`, error);
    throw error;
  }
};



// myQuizzesドキュメントから非表示クイズリストを取得するフェッチャー
const fetchHiddenQuizzes = async (userId: string): Promise<string[]> => {
  if (!userId) return [];
  const myQuizzesDocRef = doc(db, `artifacts/${appId}/users/${userId}/userData`, 'myQuizzes');
  const docSnap = await getDoc(myQuizzesDocRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    return Array.isArray(data.hiddenQuizzes) ? data.hiddenQuizzes : [];
  }
  return [];
};

/**
* ユーザーの非表示クイズリストを取得し管理するSWRフック
* @param userId ユーザーID
*/
export const useHiddenQuizzesData = (userId: string | undefined) => {
  const swrKey = userId ? `artifacts/${appId}/users/${userId}/userData/myQuizzes` : null;

  const { data, error, isLoading, mutate } = useSWR<string[]>(swrKey, fetchHiddenQuizzes, {
    revalidateOnFocus: false, // フォーカス時の再検証は不要な場合が多い
    revalidateIfStale: true, // 古いデータでも再検証する
    errorRetryInterval: 5000,
  });

  return {
    hiddenQuizzesList: data || [], // データがない場合は空配列を返す
    hiddenQuizzesLoading: isLoading,
    hiddenQuizzesError: error,
    mutateHiddenQuizzes: mutate, // キャッシュを手動で更新するためのmutate関数
  };
};
